import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API_BASE } from "../lib/api";
import { Award, CheckCircle2, ShieldAlert, ExternalLink, Headphones, BookOpen, GraduationCap } from "lucide-react";
import BrandNav from "../components/BrandNav";
import ShareRow from "../components/ShareRow";

const TYPE_LABEL = {
  level_1: "Term Completion",
  level_2: "Age-Band Graduate",
};
const GRADE_LABEL = {
  distinction: "Distinction",
  merit: "Merit",
  pass: "Pass",
};

function bandLabel(id) {
  if (!id) return "";
  return "Ages " + id.replace("age_", "").replace("-", "\u2013");
}

export default function Verify() {
  const { certId } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE}/api/certificates/verify/${encodeURIComponent(certId)}`)
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!alive) return;
        if (!res.ok) {
          setState({ loading: false, data: null, error: body.detail || "This certificate could not be verified." });
        } else {
          setState({ loading: false, data: body, error: null });
          // Fire an anonymous analytics event on successful verify view.
          fetch(`${API_BASE}/api/marketing/event`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              event_type: "certificate_cta_view",
              cert_id: certId,
              referrer: typeof document !== "undefined" ? document.referrer || "" : "",
            }),
            keepalive: true,
          }).catch(() => { /* swallow */ });
        }
      })
      .catch(() => {
        if (alive) setState({ loading: false, data: null, error: "Network error while verifying." });
      });
    return () => {
      alive = false;
    };
  }, [certId]);

  return (
    <div data-testid="verify-page">
      <BrandNav />
      <main className="ftg-shell ftg-page">
        <div className="ftg-verify">
          {state.loading && (
            <div className="ftg-loader" data-testid="verify-loading">
              <div className="ftg-loader__dot" />
            </div>
          )}

          {!state.loading && state.error && (
            <div className="ftg-verify__card ftg-verify__card--invalid" data-testid="verify-invalid">
              <ShieldAlert size={36} />
              <h1 className="ftg-verify__title">Certificate not found</h1>
              <p className="ftg-verify__body">
                We couldn&apos;t find a certificate with ID <code>{certId}</code>. Please check the ID or the QR code on the printed certificate and try again.
              </p>
              <Link to="/" className="ftg-btn ftg-btn--ghost" data-testid="verify-home">
                Back to home
              </Link>
            </div>
          )}

          {!state.loading && state.data && (
            <div className="ftg-verify__card ftg-verify__card--valid" data-testid="verify-valid">
              <div className="ftg-verify__ok">
                <CheckCircle2 size={20} /> Verified genuine
              </div>
              <Award size={44} className="ftg-verify__award" />
              <div className="ftg-verify__eyebrow">
                {TYPE_LABEL[state.data.cert_type] || state.data.cert_type}
              </div>
              <h1 className="ftg-verify__title">
                {state.data.child_first_name}
              </h1>
              <p className="ftg-verify__lede">
                {state.data.cert_type === "level_2" ? (
                  <>
                    Completed all three terms of the FutureTec Global STEAM curriculum for{" "}
                    <strong>{bandLabel(state.data.age_band)}</strong>
                    {state.data.grade && (
                      <> with <strong>{GRADE_LABEL[state.data.grade] || state.data.grade}</strong></>
                    )}
                    .
                  </>
                ) : (
                  <>
                    Completed <strong>Term {state.data.term}</strong> of the FutureTec Global STEAM curriculum for <strong>{bandLabel(state.data.age_band)}</strong>.
                  </>
                )}
              </p>
              <dl className="ftg-verify__meta">
                <div><dt>Certificate ID</dt><dd data-testid="verify-slug">{state.data.cert_id_slug}</dd></div>
                <div><dt>Issued</dt><dd>{new Date(state.data.issued_at).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}</dd></div>
                <div><dt>Issuer</dt><dd>{state.data.issuer}</dd></div>
              </dl>
              <Link to="/" className="ftg-btn ftg-btn--ghost" data-testid="verify-explore">
                <ExternalLink size={14} /> Explore FutureTec Global
              </Link>
              <div className="ftg-verify__share">
                <div className="ftg-verify__share-title">Share this certificate</div>
                <ShareRow
                  cert={state.data}
                  verifyUrl={typeof window !== "undefined" ? window.location.href : ""}
                  variant="wide"
                  testIdPrefix="verify-share"
                />
              </div>
            </div>
          )}

          {!state.loading && state.data && (
            <aside className="ftg-cta-card" data-testid="verify-cta">
              <div className="ftg-cta-card__eyebrow">Explore FutureTec Global</div>
              <h2 className="ftg-cta-card__title">
                Give your child the same STEAM foundation
                <span className="ftg-cta-card__title-thin"> &mdash; starting free.</span>
              </h2>
              <ul className="ftg-cta-card__bullets">
                <li><Headphones size={16} /> Self-guided audio lessons designed for ages 6&ndash;17.</li>
                <li><BookOpen size={16} /> Printable full-colour workbooks with answer keys.</li>
                <li><GraduationCap size={16} /> Certificates verified globally &mdash; like this one.</li>
              </ul>
              <div className="ftg-cta-card__actions">
                <Link to="/" className="ftg-btn ftg-btn--secondary" data-testid="verify-cta-start-free">
                  Start free preview
                </Link>
                <Link to="/#pricing" className="ftg-btn ftg-btn--ghost" data-testid="verify-cta-pricing">
                  See pricing
                </Link>
              </div>
            </aside>
          )}
        </div>
      </main>
    </div>
  );
}
